import {
  useNavigate,
  useLocation,
  useParams
} from 'react-router-dom';

// Normalizes navigation arguments to handle both React Router v5 and v6 formats
export const normalizeNavigationArgs = (path, stateOrOptions) => {
  const isObjectPath = path && typeof path === 'object' && !Array.isArray(path);

  let targetPath = path || '/';
  let options = {};

  if (isObjectPath) {
    // v5 object format: { pathname, state }
    targetPath = path.pathname || '/';
    if (path.state !== undefined) {
      options.state = path.state;
    }
  } else {
    // v6 format: { state, replace, relative, etc. } - preserve all options
    if (stateOrOptions && typeof stateOrOptions === 'object' && !Array.isArray(stateOrOptions)) {
      options = { ...stateOrOptions };
    }
  }

  return { targetPath, options };
};

// Compatibility HOC for withRouter (React Router v6)
// Provides backward compatibility for components using withRouter from v5
export const withRouter = (Component) => {
  const WithRouterComponent = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Create history-like object compatible with both v5 and v6 API
    const history = {
      push: (path, stateOrOptions) => {
        const { targetPath, options } = normalizeNavigationArgs(path, stateOrOptions);
        navigate(targetPath, options);
      },
      replace: (path, stateOrOptions) => {
        const { targetPath, options } = normalizeNavigationArgs(path, stateOrOptions);
        navigate(targetPath, { ...options, replace: true });
      },
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      location
    };

    const match = {
      params,
      path: location.pathname,
      url: location.pathname
    };

    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={match}
      />
    );
  };

  const componentName = Component.displayName || Component.name || 'Component';
  WithRouterComponent.displayName = `withRouter(${componentName})`;

  return WithRouterComponent;
};
